import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import Check from 'svg/check.svg?react';
import { Layout } from '../components/Layout/Layout';
import { Button } from '../components/common/Button';
import HomeButton from '../components/common/HomeButton';
import { useNavigation } from '../hooks/useNavigation';
import { setAllowQrcode, setPhotoCount } from '../store/photoSlice';
import { setPaid } from '../store/authSlice';
import { RootState } from '../store/rootReducer';
import { checkAiServer } from '../service/api.v2.service';
import {
  Container,
  CounterBox,
  SelectedThemeBox,
  Description,
  Counter,
  Count,
  CountButton,
  AccountBox,
  QrcodeAllowText,
  DeveloperModeToggle,
  CheckboxContainer,
} from './CopyPage.styled';
import Timer from '../components/common/Timer.tsx';
import { useTimeoutHandler } from '../hooks/useTimeoutHandler.ts';

export function CopyPage() {
  const { t } = useTranslation();
  const photoCount = useSelector((state: RootState) => state.photo.photoCount);
  const block = useSelector((state: RootState) => state.block.block);
  const selectedThemeId = useSelector((state: RootState) => state.theme.selectedThemeId);
  const allowQrcode = useSelector((state: RootState) => state.photo.allowQrcode);

  const [isDeveloperMode, setIsDeveloperMode] = useState(false);

  const theme = selectedThemeId
    ? block.themes.find((t) => t.id === selectedThemeId) || block.themes[0]
    : block.themes[0];

  const { maxPrintSize, minPrintSize, printStep, qrPrice, printPrice, inAppPay } = theme;
  const { style } = block;
  const { handleCopyTimeout } = useTimeoutHandler();

  const amount = photoCount === 0 ? qrPrice : photoCount * printPrice;
  const dispatch = useDispatch();
  const { navigateToPayment, navigateToStandby, navigateToTheme } = useNavigation();

  const handleDecrement = () => {
    if (photoCount - printStep < minPrintSize) {
      dispatch(setPhotoCount(minPrintSize));
      return;
    }

    dispatch(setPhotoCount(photoCount - printStep));
  };

  const handleIncrement = () => {
    if (photoCount === 0) {
      const initialValue = minPrintSize > 0 ? minPrintSize : printStep;
      dispatch(setPhotoCount(initialValue));
      return;
    }

    if (photoCount + printStep <= maxPrintSize) {
      dispatch(setPhotoCount(photoCount + printStep));
    }
  };

  useEffect(() => {
    if (minPrintSize > 0 && photoCount < minPrintSize) {
      dispatch(setPhotoCount(minPrintSize));
    }
  }, [minPrintSize, dispatch, photoCount]);

  const handleNavigation = () => {
    if (selectedThemeId) {
      if (import.meta.env.DEV && isDeveloperMode) {
        console.log('🔧 개발자 모드: 결제 프로세스 스킵');
        dispatch(setPaid(true));
        navigateToStandby();
        return;
      }

      checkAiServer(selectedThemeId)
        .then(() => {
          if (inAppPay && amount > 0) {
            navigateToPayment();
          } else {
            navigateToStandby();
          }
        })
        .catch((error) => {
          console.error('AI 서버 체크 실패:', error);
          if (inAppPay && amount > 0) {
            navigateToPayment();
          } else {
            navigateToStandby();
          }
        });
    }
  };

  return (
    <Layout>
      <HomeButton />
      <Timer initialTime={40} onTimeout={handleCopyTimeout} />
      <Container>
        <SelectedThemeBox>
          <p>{t('copy.selectedTheme')}</p>
          <div>
            <img src={theme.themeImage || block?.logo} alt={t('copy.selectedTheme')} />
            <button onClick={navigateToTheme}>{t('copy.reselectTheme')}</button>
          </div>
        </SelectedThemeBox>
        <CounterBox>
          <Description>
            <p>{t('copy.selectPhotoCount')}</p>
            <p>{t('copy.qrCodeInfo')}</p>
          </Description>
          <Counter>
            <CountButton disabled={photoCount <= minPrintSize} onClick={handleDecrement}>
              -
            </CountButton>

            <Count>
              {photoCount === 0
                ? '0'
                : printStep === 1
                  ? photoCount
                  : `${photoCount / 2} + ${photoCount / 2}`}
            </Count>

            <CountButton disabled={photoCount + printStep > maxPrintSize} onClick={handleIncrement}>
              +
            </CountButton>
          </Counter>
          <AccountBox>{inAppPay ? `₩ ${amount.toLocaleString('en-US')}` : `Free`}</AccountBox>
        </CounterBox>
        {import.meta.env.DEV ? (
          <CheckboxContainer>
            <QrcodeAllowText
              checked={allowQrcode}
              onClick={() => dispatch(setAllowQrcode(!allowQrcode))}
            >
              <Check fill={allowQrcode ? style[2] : style[7]} />
              {t('copy.qrCodeAgreement')}
            </QrcodeAllowText>
            <DeveloperModeToggle
              checked={isDeveloperMode}
              onClick={() => setIsDeveloperMode(!isDeveloperMode)}
            >
              <Check fill={isDeveloperMode ? '#FF6B35' : style[7]} />
              🔧 개발자 모드
            </DeveloperModeToggle>
          </CheckboxContainer>
        ) : (
          <QrcodeAllowText
            checked={allowQrcode}
            onClick={() => dispatch(setAllowQrcode(!allowQrcode))}
            style={{ marginBottom: '22px' }}
          >
            <Check fill={allowQrcode ? style[2] : style[7]} />
            {t('copy.qrCodeAgreement')}
          </QrcodeAllowText>
        )}
        <Button disabled={!allowQrcode} onClick={handleNavigation}>
          {inAppPay && amount > 0
            ? import.meta.env.DEV && isDeveloperMode
              ? '결제 스킵하고 시작하기'
              : t('copy.startPayment')
            : t('copy.complete')}
        </Button>
      </Container>
    </Layout>
  );
}
